import {
  isWhatsAppMsPlatform,
  toSidebarPlatformKey
} from './inboxMsChatPlatform';

/**
 * Timestamp del último mensaje del chat MS; 0 si no hay fecha válida.
 * @param {Object} chat - Item JSON:API `{ id, attributes }` del inbox MS.
 * @returns {number}
 */
function lastMessageTimestamp(chat) {
  const timestamp = new Date(chat?.attributes?.recent_message_date).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

/**
 * Id canónico del chat (raíz JSON:API o attributes.id).
 * @param {Object} chat
 * @returns {string|number|undefined}
 */
export function chatIdFromRecord(chat) {
  return chat?.id ?? chat?.attributes?.id;
}

/**
 * Ordena chats del MS del más reciente al más antiguo.
 * @param {Array<Object>} chats
 * @returns {Array<Object>}
 */
function sortByRecent(chats) {
  return [...chats].sort((a, b) => lastMessageTimestamp(b) - lastMessageTimestamp(a));
}

/**
 * ¿El chat pertenece a la misma familia de canal que `platform`?
 * WhatsApp (BSP/QR/Gupshup/`whatsapp`) se trata como un solo canal.
 * @param {Object} chat
 * @param {string|undefined|null} platform
 * @returns {boolean}
 */
function matchesPlatformFamily(chat, platform) {
  const chatPlatform = chat?.attributes?.platform;
  if (!platform || !chatPlatform) return false;
  if (chatPlatform === platform) return true;

  if (isWhatsAppMsPlatform(platform)) {
    return isWhatsAppMsPlatform(chatPlatform);
  }

  return toSidebarPlatformKey(chatPlatform) === toSidebarPlatformKey(platform);
}

/**
 * Elige qué chat del MS abrir al entrar desde CRM / deals.
 *
 * En multinúmero el índice CRM solo guarda un `chat_id` + `platform` por fila en
 * `customer_inbox_chats`, y ese par puede estar desfasado respecto al MS (mismo
 * contacto en varios números). La fuente de verdad es el listado del MS:
 *
 * 1. Match exacto por `conversationId` → usa la plataforma real del MS.
 * 2. Si no hay id (o no aparece), el más reciente de la misma familia de canal.
 * 3. Si tampoco, el más reciente de todos.
 *
 * @param {Array<Object>} chats - Items de `GET /chats/inbox?customer_id=...`.
 * @param {{ conversationId?: string|number|null, platform?: string|null }} options
 * @returns {Object|null} Chat JSON:API a abrir, o null si la lista está vacía.
 */
export function resolveCrmInboxChat(chats, { conversationId = null, platform = null } = {}) {
  if (!Array.isArray(chats) || chats.length === 0) return null;

  if (conversationId != null && conversationId !== '') {
    const wanted = String(conversationId);
    const byId = chats.find((chat) => String(chatIdFromRecord(chat)) === wanted);
    if (byId) return byId;
  }

  const family = chats.filter((chat) => matchesPlatformFamily(chat, platform));
  if (family.length > 0) return sortByRecent(family)[0];

  return sortByRecent(chats)[0];
}

export default resolveCrmInboxChat;
